// frontend/src/pages/SiteMessages.js

import React, { useState, useEffect } from 'react';
import { Container, Card, ListGroup, Badge, Button, Modal, Alert } from 'react-bootstrap';
import { AuthService } from "../services";
import UserService from "../services/user.service";   // ← correct service
import AppHeader from '../components/AppHeader';
import './SiteMessages.css';

const SiteMessages = (props) => {
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState('all'); // all | unread | read
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const currentUser = AuthService.getCurrentUser();

  useEffect(() => {
    if (!currentUser) {
      props.history.push("/login");
      return;
    }
    loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMessages = () => {
    setLoading(true);
    UserService.getUserMessages()
      .then((response) => {
        setMessages(Array.isArray(response.data) ? response.data : []);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error loading messages:", error);
        setMessages([]);
        setLoading(false);
      });
  };

  const markAsRead = async (messageId) => {
    try {
      await UserService.markMessageAsRead(messageId);
      setMessages((prev) =>
        prev.map((msg) => (msg._id === messageId ? { ...msg, isRead: true } : msg))
      );
    } catch (error) {
      console.error("Error marking message as read:", error);
    }
  };

  const markAllAsRead = async () => {
    const unread = messages.filter((msg) => !msg.isRead);
    for (const msg of unread) {
      try {
        await UserService.markMessageAsRead(msg._id);
      } catch (e) {
        console.error("Error marking message as read:", e);
      }
    }
    setMessages((prev) => prev.map((msg) => ({ ...msg, isRead: true })));
  };

  const openMessage = (message) => {
    setSelectedMessage(message);
    setShowMessageModal(true);
    if (!message.isRead) {
      markAsRead(message._id);
    }
  };

  const getFilteredMessages = () => {
    if (filter === 'unread') return messages.filter((m) => !m.isRead);
    if (filter === 'read')   return messages.filter((m) =>  m.isRead);
    return messages;
  };

  const getMessageTypeBadge = (type) => {
    const map = {
      recharge:   <Badge bg="success">Recharge</Badge>,
      withdrawal: <Badge bg="info">Withdrawal</Badge>,
      trade:      <Badge bg="warning">Trade</Badge>,
      system:     <Badge bg="secondary">System</Badge>,
      admin:      <Badge bg="primary">📢 Admin</Badge>,
    };
    return map[type] || <Badge bg="dark">General</Badge>;
  };

  // Show sentAt first, fall back to createdAt
  const formatDate = (msg) => {
    const raw = msg.sentAt || msg.createdAt;
    if (!raw) return 'N/A';
    const d = new Date(raw);
    return d.toLocaleString(undefined, {
      year:   'numeric',
      month:  'short',
      day:    'numeric',
      hour:   '2-digit',
      minute: '2-digit',
    });
  };

  const getUnreadCount = () => messages.filter((m) => !m.isRead).length;

  const filteredMessages = getFilteredMessages();

  return (
    <div className="site-messages-page">
      <AppHeader />

      <Container className="mt-4">
        {/* Page header */}
        <div className="page-header mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2>Site Messages</h2>
              <p className="text-muted mb-0">
                Notifications and messages from the admin team
              </p>
            </div>
            <div className="message-actions">
              {getUnreadCount() > 0 && (
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={markAllAsRead}
                  className="me-2"
                >
                  Mark All as Read
                </Button>
              )}
              <Badge bg="primary" className="unread-count">
                {getUnreadCount()} unread
              </Badge>
            </div>
          </div>
        </div>

        {/* Filter tabs */}
        <Card className="mb-4">
          <Card.Body className="p-3">
            <div className="filter-tabs">
              <Button
                variant={filter === 'all' ? 'primary' : 'outline-primary'}
                onClick={() => setFilter('all')}
                className="me-2"
              >
                All ({messages.length})
              </Button>
              <Button
                variant={filter === 'unread' ? 'warning' : 'outline-warning'}
                onClick={() => setFilter('unread')}
                className="me-2"
              >
                Unread ({getUnreadCount()})
              </Button>
              <Button
                variant={filter === 'read' ? 'success' : 'outline-success'}
                onClick={() => setFilter('read')}
              >
                Read ({messages.length - getUnreadCount()})
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* Messages list */}
        <Card>
          <Card.Header>
            <h5 className="mb-0">
              {filter === 'all'    && 'All Messages'}
              {filter === 'unread' && 'Unread Messages'}
              {filter === 'read'   && 'Read Messages'}
            </h5>
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading messages...</p>
              </div>
            ) : filteredMessages.length === 0 ? (
              <div className="text-center py-5">
                <h5 className="text-muted mt-3">No messages found</h5>
                <p className="text-muted">
                  {filter === 'all'
                    ? "You don't have any messages yet."
                    : `No ${filter} messages found.`}
                </p>
              </div>
            ) : (
              <ListGroup variant="flush">
                {filteredMessages.map((message) => (
                  <ListGroup.Item
                    key={message._id}
                    className={`message-item ${!message.isRead ? 'unread' : ''}`}
                    onClick={() => openMessage(message)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex align-items-start">
                      <div className="message-content flex-grow-1">
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <h6 className="message-title mb-0">
                            {message.title}
                            {!message.isRead && (
                              <Badge bg="danger" className="ms-2">New</Badge>
                            )}
                          </h6>
                          {/* Timestamp — sentAt preferred over createdAt */}
                          <small className="message-time text-muted ms-3" style={{ whiteSpace: 'nowrap' }}>
                            🕐 {formatDate(message)}
                          </small>
                        </div>

                        <p className="message-preview mb-1">
                          {message.message && message.message.length > 100
                            ? message.message.substring(0, 100) + '...'
                            : message.message || 'No content'}
                        </p>

                        <div className="message-meta d-flex align-items-center gap-2">
                          {getMessageTypeBadge(message.messageType)}
                          <small className="text-muted">Click to read full message</small>
                        </div>
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* Message detail modal */}
      <Modal
        show={showMessageModal}
        onHide={() => setShowMessageModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedMessage && selectedMessage.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMessage && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                {getMessageTypeBadge(selectedMessage.messageType)}
                <small className="text-muted">
                  🕐 Sent: {formatDate(selectedMessage)}
                </small>
              </div>

              <div className="message-body">
                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                  {selectedMessage.message || 'No content available'}
                </p>
              </div>

              {selectedMessage.relatedId && (
                <Alert variant="info" className="mt-3 mb-0">
                  <small>
                    <strong>Related Transaction:</strong> {selectedMessage.relatedId}
                  </small>
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMessageModal(false)}>
            Close
          </Button>
          {selectedMessage && !selectedMessage.isRead && (
            <Button
              variant="success"
              onClick={() => {
                markAsRead(selectedMessage._id);
                setShowMessageModal(false);
              }}
            >
              Mark as Read & Close
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SiteMessages;
